/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid, react/self-closing-comp */
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import {
  FlexContainer,
  PriceContainer,
  PriceExpandContainer,
  PriceText,
  PriceTitle,
  TotalText,
  TotalTitle,
  PaddingContainer,
  PriceExpandContent,
  TimeTitle,
  TimeText,
  DeliveryTimeContainer,
  Trigger,
  PayButtonSupportText,
} from './parcel-creation-bottom.styles';
import { useStore } from '../../../stores/store';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import { ReactComponent as AirplaneIcon } from '../../../assets/airplane.svg';
import { ReactComponent as ShipIcon } from '../../../assets/ship.svg';
import { ReactComponent as RocketIcon } from '../../../assets/rocket.svg';
import { ReactComponent as CarIcon } from '../../../assets/car.svg';
import { ReactComponent as ChevronDown } from '../../../assets/chevron-down.svg';
import { DeliveryMethod } from '../../../models/delivery-options/common';
import { LinkSmall } from '../../../common/typography/typography.styles';
import { MarginContainer } from '../../../common/base-margin/base-margin.component';
import { ShipmentPriceDetails } from '../../../models/parcelDeliveryMiles';

export const MiddleMileIcon: React.FC<{
  method: DeliveryMethod | undefined;
}> = ({ method }) => {
  switch (method) {
    case 'air':
      return <AirplaneIcon />;
    case 'car':
      return <CarIcon />;
    case 'sea':
      return <ShipIcon />;
    case 'expedited':
      return <RocketIcon />;
    default:
      return null;
  }
};

const canadaDomesticLink =
  'https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses/charge-collect-which-rate/calculator.html#clcltr';

const ParcelCreationPriceBlock = () => {
  const {
    userStore: { getCurrencySymbol },
    parcelCreationStore: {
      shipment,
      selectedDeliveryOption,
      countryDeparture,
      isDomestic,
    },
  } = useStore();
  const [isPriceContainerExpanded, setIsPriceContainerExpanded] =
    useState(false);

  const { t } = useTranslation();

  const shipmentCurrencyCode = shipment?.converted_price?.currency;

  const deliveryTimeBlock = (
    <>
      <TimeTitle>
        {shipment?.delivery_time_title?.delivery_time_title}
      </TimeTitle>
      <TimeText>
        <MiddleMileIcon
          method={selectedDeliveryOption?.middle_mile.delivery_method}
        />
        <p>{shipment?.delivery_time_title?.delivery_time_details}</p>
      </TimeText>
    </>
  );

  const showCanadaDomesticSupportText =
    isDomestic && countryDeparture?.code === 'CA';

  const formatPriceTitle = (price: ShipmentPriceDetails) =>
    price.type === 'first_mile' || price.type === 'last_mile'
      ? price.description?.replace(' - ', ': ')
      : price.description;

  return (
    <>
      <PriceContainer>
        {selectedDeliveryOption?.selectedFirstMile && (
          <DeliveryTimeContainer
            $pr='2.4rem'
            $pl='2.4rem'>
            {deliveryTimeBlock}
          </DeliveryTimeContainer>
        )}
        <PaddingContainer
          $pr='2.4rem'
          $pl='2.4rem'>
          <Trigger
            aria-expanded={isPriceContainerExpanded}
            aria-controls='price-details'
            aria-label='Accordion trigger'
            onClick={() => setIsPriceContainerExpanded((prev) => !prev)}
            $expanded={isPriceContainerExpanded}>
            <ChevronDown />
            <TotalTitle>{t('total')}</TotalTitle>
          </Trigger>
          <TotalText>
            {getFormattedPrice(
              shipment?.converted_price?.total_price,
              getCurrencySymbol(shipmentCurrencyCode)
            )}
          </TotalText>
        </PaddingContainer>
      </PriceContainer>

      <PriceExpandContainer
        id='price-details'
        $expanded={isPriceContainerExpanded}>
        <PriceExpandContent>
          <span className='separatorLine' />
          {shipment?.price_details?.map((price) => (
            <FlexContainer key={price.type + price.value}>
              <PriceTitle>{formatPriceTitle(price)}</PriceTitle>
              <PriceText $isRed={price.type === 'promo'}>
                {getFormattedPrice(
                  price.value,
                  getCurrencySymbol(shipmentCurrencyCode)
                )}
              </PriceText>
            </FlexContainer>
          ))}

          {showCanadaDomesticSupportText && (
            <MarginContainer>
              <PayButtonSupportText>
                <Trans
                  i18nKey='ca_domestic_total_block_taxes_text'
                  components={{
                    // eslint-disable-next-line
                    1: (
                      <LinkSmall
                        underline='hover'
                        target='_blank'
                        rel='noopener noreferrer'
                      />
                    ),
                  }}
                  values={{
                    taxCalculatorLink: canadaDomesticLink,
                  }}
                />
              </PayButtonSupportText>
            </MarginContainer>
          )}
        </PriceExpandContent>
      </PriceExpandContainer>
    </>
  );
};

export default observer(ParcelCreationPriceBlock);
